import type * as A from '@/api';
import { Auth } from '@/aws-config';
import { logger } from '@/utils/logger';
import { graphql } from '@/utils/graphql';
import Workspace from './workspace';
import WorkspaceLite from './workspaceLite';
import User from './user';
import { handleSystemError } from '@/utils/handleError';
import { assertIsDefined } from '@/utils/typeChecks';

type OrganizationConstructorInput = A.GetOrganizationQuery['getOrganization'];

const ns = 'app.services.organization';

const NO_CURRENT_USER = 'No current user';

// Amplify signals "nobody is signed in" as the bare string on some code paths
// and as an Error carrying the same message on others.
function isNoCurrentUserError(e: unknown): boolean {
  if (e === NO_CURRENT_USER) return true;
  return e instanceof Error && e.message === NO_CURRENT_USER;
}

export default class Organization {
  readonly id: string;

  readonly name: string;

  readonly color: string;

  readonly workspaceLimit: number;

  readonly connectionLimit: number;

  // How many days of generative-dashboard edit history this org keeps.
  // Backfilled from Chargebee by the billing sync (Essentials → 14,
  // Pro → 90, Enterprise → 365). Null when no sync has populated it
  // yet — consumers should treat null as "fall back to default".
  readonly editHistoryRetentionDays: number | null;

  #warehouseRetention: number;
  get warehouseRetention(): number {
    return this.#warehouseRetention;
  }
  private set warehouseRetention(i: number) {
    this.#warehouseRetention = i;
  }

  readonly timezone: string;

  readonly maintenanceWindow: string | undefined;

  readonly slug: string;

  readonly cloudResourceId: string;

  readonly wholeSourceConfig: string;

  readonly wholeSourceConfigVersion: number;

  readonly terraformConfig: string;

  readonly terraformConfigVersion: number;

  readonly workspaces: WorkspaceLite[];

  #isUsersLoaded: boolean;

  #users: User[];

  get users(): User[] {
    if (this.#isUsersLoaded) return this.#users;
    throw new Error('Unexpected - Organization - get users() - !#isUsersLoaded');
  }

  set users(newUsers: User[]) {
    this.#users = newUsers;
  }

  constructor(input: OrganizationConstructorInput) {
    this.id = input.id;
    this.name = input.name;
    this.color = input.color;
    this.workspaceLimit = input.workspaceLimit ?? 1;
    this.connectionLimit = input.connectionLimit ?? 1000;
    this.editHistoryRetentionDays =
      (input as { editHistoryRetentionDays?: number | null }).editHistoryRetentionDays ?? null;
    this.#warehouseRetention = input.warehouseRetention ?? 90;
    this.timezone = input.timezone;
    this.maintenanceWindow = input.maintenanceWindow;
    this.slug = input.slug;
    this.cloudResourceId = input.cloudResourceId;
    this.wholeSourceConfig = input.wholeSourceConfig;
    this.wholeSourceConfigVersion = input.wholeSourceConfigVersion;
    this.terraformConfig = input.terraformConfig ?? '{}';
    this.terraformConfigVersion = input.terraformConfigVersion ?? 0;
    this.workspaces = input.workspaces?.items.map((el) => new WorkspaceLite(el)) ?? [];
    if (input.users?.items) {
      this.#isUsersLoaded = true;
      this.#users = input.users?.items.map((el) => new User(el.user));
    } else {
      this.#isUsersLoaded = false;
      this.#users = [];
    }

    this.getWarehouseStorageInfo = this.getWarehouseStorageInfo.bind(this);
    this.setWarehouseRetention = this.setWarehouseRetention.bind(this);
    this.createFivetranGroupIdIfNotExist = this.createFivetranGroupIdIfNotExist.bind(this);
    this.removeUserFromAllWorkspaces = this.removeUserFromAllWorkspaces.bind(this);
    this.removeUser = this.removeUser.bind(this);
    this.getChargebeeInfo = this.getChargebeeInfo.bind(this);
    this.getBillingSummary = this.getBillingSummary.bind(this);
    this.createBillingPortalSession = this.createBillingPortalSession.bind(this);
    this.getDpuRows = this.getDpuRows.bind(this);
    this.getInvoiceDownloadUrl = this.getInvoiceDownloadUrl.bind(this);
  }

  async getWarehouseStorageInfo(): Promise<A.GetWarehouseStorageInfoOutput> {
    return await graphql('getWarehouseStorageInfo', {
      query: { orgId: this.cloudResourceId },
    });
  }

  /**
   * Plan and recent invoices, for the Usage & Billing tab.
   *
   * Keyed on `this.id` — the Organization UUID — not `cloudResourceId`, because the resolver
   * reads `OrganizationChargebee` (whose key is that UUID) and authorises against the
   * `beem:orgAdmin` claim, which also carries UUIDs. Passing the slug would fail the guard and
   * look like a permissions bug.
   *
   * Every money figure on the page comes back from here, i.e. from Chargebee. Nothing about a
   * price is computed in the browser, so the page cannot show a total that disagrees with the
   * invoice.
   */
  async getBillingSummary(invoiceLimit?: number): Promise<A.GetBillingSummaryOutput> {
    return await graphql('getBillingSummary', {
      query: { orgId: this.id, invoiceLimit },
    });
  }

  /**
   * A short-lived authenticated Chargebee portal URL.
   *
   * Valid for **1 hour** and **single-use** (Chargebee: "Once accessed, the access url cannot be
   * reused. Hence do not persist this URL."). An hour is long enough to mint on load and render a
   * real `href`, which is what lets the affordance be a link rather than a click handler. Do not
   * cache the result: a second reader would get a URL someone else has already spent.
   */
  async createBillingPortalSession(
    redirectUrl?: string,
  ): Promise<A.CreateBillingPortalSessionOutput> {
    return await graphql('createBillingPortalSession', {
      query: { orgId: this.id, redirectUrl },
    });
  }

  /**
   * A short-lived PDF link for one of this organisation's invoices.
   *
   * Minted per request and never cached: Chargebee's link lasts about an hour, and caching one
   * would hand a second reader a URL the first may already have spent.
   *
   * The resolver checks that the invoice belongs to *this* org's subscription, so a caller
   * cannot read another customer's invoice by guessing an id — being an org admin authorises you
   * for your own invoices, not for arbitrary invoice ids.
   */
  async getInvoiceDownloadUrl(invoiceId: string): Promise<A.GetInvoiceDownloadUrlOutput> {
    return await graphql('getInvoiceDownloadUrl', {
      query: { orgId: this.id, invoiceId },
    });
  }

  /**
   * Daily DPU and storage rows for a date range, inclusive at both ends.
   *
   * `date` is the sort key on `byOrganization`, so this is a key condition rather than a filter —
   * a filter would page through every row the org has ever had and discard most of them.
   */
  async getDpuRows(
    from: string,
    to: string,
  ): Promise<
    Array<{
      date: string;
      dpu: number;
      storage?: number | null;
      warehouseStorageMb?: number | null;
    }>
  > {
    const rs = await graphql('listOrganizationDPUsByOrganization', {
      organizationId: this.id,
      date: { between: [from, to] },
    });
    return rs?.items ?? [];
  }

  async getWarehouseConnectionInfo(): Promise<A.GetWarehouseConnectionInfoOutput> {
    return await graphql('getWarehouseConnectionInfo', {
      query: { orgId: this.cloudResourceId },
    });
  }

  async setWarehouseRetention(daysToRetainData: number): Promise<void> {
    await graphql('updateOrganizationSettings', {
      query: { orgId: this.cloudResourceId, daysToRetainData },
    });
    await graphql('updateOrganization', {
      input: { id: this.id, warehouseRetention: daysToRetainData },
    });
    this.warehouseRetention = daysToRetainData;
  }

  async updateWholeSourceConfig(wholeSourceConfig: string): Promise<void> {
    const currentVersion = this.wholeSourceConfigVersion;
    const previousConfig = this.wholeSourceConfig;

    // Get current user
    const cognitoUser = await Auth.currentUserInfo();
    const userId = cognitoUser?.username;

    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Update the organization with version check
    await graphql('updateOrganization', {
      input: {
        id: this.id,
        wholeSourceConfig,
        wholeSourceConfigVersion: currentVersion + 1,
      },
      condition: {
        wholeSourceConfigVersion: {
          eq: currentVersion,
        },
      },
    });

    // Create history record
    await graphql('createOrganizationSourceConfigHistory', {
      input: {
        organizationId: this.id,
        userId,
        wholeSourceConfig: previousConfig,
        wholeSourceConfigVersion: currentVersion,
      },
    });
  }

  async updateTerraformConfig(terraformConfig: string): Promise<void> {
    const currentVersion = this.terraformConfigVersion;
    const previousConfig = this.terraformConfig;

    // Get current user
    const cognitoUser = await Auth.currentUserInfo();
    const userId = cognitoUser?.username;

    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Update the organization with version check
    await graphql('updateOrganization', {
      input: {
        id: this.id,
        terraformConfig,
        terraformConfigVersion: currentVersion + 1,
      },
      condition: {
        terraformConfigVersion: {
          eq: currentVersion,
        },
      },
    });

    // Create history record
    await graphql('createOrganizationTerraformConfigHistory', {
      input: {
        organizationId: this.id,
        userId,
        terraformConfig: previousConfig,
        terraformConfigVersion: currentVersion,
      },
    });
  }

  async createFivetranGroupIdIfNotExist(): Promise<string> {
    const rs = await graphql('getOrganizationFivetran', { id: this.id });
    if (rs) return rs.groupId;
    const rs2 = await graphql('fivetranCreateGroup', {
      input: { id: this.id, orgId: this.cloudResourceId },
    });
    return rs2.groupId;
  }

  async removeUserFromAllWorkspaces(userId: string): Promise<void> {
    logger.info({ label: `${ns}.removeUserFromAllWorkspaces.userId`, message: userId });
    const temp = await Workspace.getWorkspacesByUser(userId);
    const workspaces = temp.filter((el) => el.organization.id === this.id);
    if (workspaces.length === 0) return;
    const lastWorkspace = workspaces.pop();
    assertIsDefined(lastWorkspace, 'BEEM240217084103');
    await Promise.all(
      workspaces.map(async (workspace) => {
        await workspace.removeMembers([userId]);
      }),
    );
    await lastWorkspace.removeMembers([userId]);
  }

  async removeUser(userId: string): Promise<void> {
    logger.info({ label: `${ns}.removeUser.userId`, message: userId });
    // vinhf migrate ID of OrganizationUser to be ${orgId}.${userId}
    const { items } = await graphql('listOrganizationUsersByUser', {
      userId,
      organizationId: { eq: this.id },
    });
    // Idempotent: the OrganizationUser link may already be gone if another
    // admin removed this user concurrently. Treat 0 matches as a no-op — the
    // user already has no org access, which is the desired end state — instead
    // of surfacing a raw BEEM code. More than 1 match means duplicate links,
    // which is a genuine data-integrity problem worth failing on.
    if (items.length > 1) throw new Error('BEEM240217093041');
    if (items.length === 0) {
      logger.info({ label: `${ns}.removeUser.alreadyRemoved`, message: userId });
      return;
    }
    const { id: organizationUserId } = items[0];
    await graphql('deleteOrganizationUser', { input: { id: organizationUserId } });
    await graphql('beemAgentAdminLogoutUser', { query: { userId } });

    // Once the user belongs to no organization at all they are fully offboarded. Mark the User row
    // inactive rather than deleting it: datasets and connections they own point at it, and a
    // dangling ownerId used to break entire folder listings (see [note 8] in schema.graphql).
    // `graphql()` follows nextToken, so this is every organization the user is still in
    // Only BeemAdministrators may update a User row, and an OrgAdmin's list only sees their own
    // org's links, so for a customer admin this step is both unreliable and refused. The removal
    // above has already happened by then: failing here showed an error for a removal that worked.
    try {
      const remaining = await graphql('listOrganizationUsersByUser', { userId });
      if (remaining.items.length === 0) {
        logger.info({ label: `${ns}.removeUser.markingInactive`, message: userId });
        await graphql('updateUser', { input: { id: userId, isActive: false } });
      }
    } catch (e) {
      logger.error({ label: `${ns}.removeUser.markingInactive.error`, message: e });
    }
  }

  /**
   * `usageAddons` comes back too, because the **storage** allowance lives in there rather than on
   * the plan — Chargebee records it nowhere, which is why the metering lambda reads it from the
   * same JSON blob. `allowance` on the record is DPU only.
   */
  async getChargebeeInfo(): Promise<{
    allowance: number;
    billingCycleDate: number;
    usageAddons?: string | null;
  }> {
    const params = { id: this.id };
    const { allowance, billingCycleDate, usageAddons } = await graphql(
      'getOrganizationChargebee',
      params,
    );
    return { allowance, billingCycleDate, usageAddons };
  }

  static async getDpuAllowanceAndBilling(id: string) {
    const { allowance, billingCycleDate } = await graphql('getOrganizationChargebee', { id });
    return { allowance, billingCycleDate };
  }

  static async getDpuAndDatalakeUsagesByOrganizationId(
    organizationId: string,
    start: Date,
  ): Promise<{ dpu: number; datalake: number }> {
    const { items } = await graphql('listOrganizationDPUsByOrganization', {
      organizationId,
      date: { gt: new Date(start.getTime() - 24 * 60 * 60 * 1000).toISOString() },
    });
    const result = items.reduce(
      (acc, cur) => {
        return {
          dpu: acc.dpu + cur.dpu,
          datalake: acc.datalake + (cur.storage ?? 0),
        };
      },
      { dpu: 0, datalake: 0 },
    );
    return { dpu: result.dpu, datalake: Math.round(result.datalake / Math.max(items.length, 1)) }; // average datalake usage
  }

  static async getOrganizationById(id: string): Promise<Organization> {
    logger.info({ label: 'app.services.organization.getOrganizationById.id', message: id });
    const data = await graphql('getOrganization', { id });
    return new Organization(data);
  }

  static async getOrganizationBySlug(slug: string): Promise<Organization | null> {
    logger.info({ label: `${ns}.getOrganizationBySlug.id`, message: slug });
    const { items } = await graphql('listOrganizationsBySlug', { slug });
    if (items.length === 0) return null;
    return new Organization(items[0]);
  }

  // undefined when nobody is signed in, null when the load failed. SecurityLayout shows its
  // load-failed state for null, and a signed-out visit must not flash it on the way to the login
  // page, so the two are kept apart.
  static async getWorkspacesOfCurrentUser(): Promise<Workspace[] | null | undefined> {
    try {
      const user = await Auth.currentUserInfo();
      logger.info({ label: `${ns}.getWorkspacesOfCurrentUser.user`, message: user });
      if (!user) return undefined;
      // NOTE: must `await` so a rejected query is caught here rather than
      // escaping to the saga/root layout and blanking the app.
      return await Workspace.getWorkspacesByUser(user.username);
    } catch (e) {
      // Returning null here makes the root layout render its "couldn't load your
      // workspaces" state, so this is the last place the real cause is visible.
      // Report it to Sentry (handleUserError does NOT) or we are blind to it -
      // this path produced a silent infinite spinner in BEEM-2279.
      //
      // Not signed in is an ordinary state, not a fault: skip reporting it.
      // Amplify raises it as the bare string on some paths and as an Error on
      // others, so check both rather than only the string.
      if (isNoCurrentUserError(e)) return undefined;
      handleSystemError(e, `${ns}.getWorkspacesOfCurrentUser.error`);
      return null;
    }
  }
}
